import type { AdminOrderSearchCriterion, OrderStatus } from '@lds-efsp/shared-contracts';
import type { AdminOrderRow } from '../../../api/useAdminOrdersApi';

// Re-exported for the property shard and the unit tests: they tether to this
// module rather than to the hook, which drags React into a node context.
export type { AdminOrderRow };

/**
 * Pure view state for the admin orders list — which of the list's three views
 * renders, and which rows each sub-nav tab shows. React-free and synchronous
 * on purpose: the page calls it on every render, and the SPEC-SOR-365 property
 * shard imports it into a bare node context (its two imports are type-only and
 * erase at emit, so the module carries no runtime dependencies).
 */

// View tab to status mapping for filtering. `null` means the tab does not
// filter by status — cases keys on case-number presence (matchesViewTab
// branches on it before consulting the map). Moved from AdminOrdersList.tsx,
// which wrote it against demo data; the unit test pins every non-null value
// to the real orderStatusEnum.
export const VIEW_STATUS_MAP: Record<string, OrderStatus[] | null> = {
  incoming: ['submitted'],
  pending: ['in-progress', 'pending-review'],
  executed: ['accepted'],
  closed: ['completed'],
  cases: null,
};

/** The search the list is filtered by, once the URL carries one. */
export interface AdminOrdersActiveSearch {
  criterion: AdminOrderSearchCriterion;
  term: string;
}

/**
 * Exactly one of three views renders: the unfiltered rows when no search is
 * active, the matching rows once one is, or the empty search result when an
 * active search matched nothing. An empty LIVE table with no search is still
 * `unfiltered` — the empty search result is a state of the search, not of the
 * table. Discriminate on `kind`, never on row count.
 */
export type AdminOrdersView =
  | { kind: 'unfiltered'; rows: readonly AdminOrderRow[] }
  | { kind: 'filtered'; rows: readonly AdminOrderRow[] }
  | { kind: 'empty' };

export function decideAdminOrdersView(
  rows: readonly AdminOrderRow[],
  search: AdminOrdersActiveSearch | null,
): AdminOrdersView {
  if (search === null) return { kind: 'unfiltered', rows };
  if (rows.length === 0) return { kind: 'empty' };
  return { kind: 'filtered', rows };
}

/**
 * Whether the row belongs to the sub-nav tab. `cases` keys on case-number
 * presence (the nullable column, so a real unassigned row is null — the demo
 * rows' 'Not Assigned' sentinel is gone with the demo shape); an unknown or
 * catch-all tab renders everything.
 */
export function matchesViewTab(row: AdminOrderRow, tab: string): boolean {
  if (tab === 'cases') return row.caseNumber !== null && row.caseNumber !== '';
  const statuses = VIEW_STATUS_MAP[tab];
  if (!statuses) return true;
  return statuses.includes(row.status);
}
